import { debounce } from "@wise-emotions/web-utils";
import { useEffect, useRef, useState } from "react";

/**
 * Creates a custom hook for handling horizontal scrolling.
 *
 * @param {number} pixelScrollValue - Optional. The number of pixels to scroll by. If not provided, the width of the wrapper element will be used.
 */
export const useHorizontalScroll = (pixelScrollValue?: number) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [scrollable, setScrollable] = useState({ left: false, right: true });
  const getPixelScrollValue = () => {
    if (pixelScrollValue !== undefined) return pixelScrollValue;
    if (wrapperRef.current) return Math.floor(wrapperRef.current.clientWidth);
    return 0;
  };
  const maxScrollLeft = wrapperRef.current
    ? wrapperRef.current.scrollWidth - wrapperRef.current.clientWidth
    : undefined;

  const scrollLeft = () => {
    wrapperRef.current?.scrollBy({ left: -getPixelScrollValue() });
  };

  const scrollToStart = () => {
    wrapperRef.current?.scrollTo({ left: 0 });
  };

  const scrollRight = () => {
    wrapperRef.current?.scrollBy({ left: getPixelScrollValue() });
  };

  const scrollToEnd = () => {
    wrapperRef.current?.scrollTo({ left: maxScrollLeft });
  };

  useEffect(() => {
    if (!wrapperRef.current) return () => null;

    const tagWrapper = wrapperRef.current;

    const handleScroll = debounce(() => {
      setScrollable({
        left: tagWrapper.scrollLeft > 0,
        right:
          // flex-shrink: 0, cause a little problem with scrollLeft, so we have to add a 0.5px to make it work
          tagWrapper.scrollLeft + 0.5 <
          tagWrapper.scrollWidth - tagWrapper.clientWidth,
      });
    }, 50);

    tagWrapper.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => {
      tagWrapper.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return {
    wrapperRef,
    scrollable,
    maxScrollLeft,
    scrollLeft,
    scrollRight,
    scrollToStart,
    scrollToEnd,
  };
};
